import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { can } from '../utils/adminPermissions';
import {
  listPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  togglePromotionStatus,
  listStoreApplications,
  approveApplication,
  rejectApplication,
  uploadPromotionImage,
  getPromotionImageUrl,
  deletePromotionImage,
} from '../services/adminPromotionsService';
import './PromotionsPage.css';

// ── Constants ───────────────────────────────────────────────

const PRESET_COLORS = [
  { label: 'Forest Green', value: '#065F46' },
  { label: 'Amber', value: '#B45309' },
  { label: 'Ocean', value: '#0E7490' },
  { label: 'Purple', value: '#6D28D9' },
  { label: 'Rose', value: '#BE123C' },
  { label: 'Indigo', value: '#3730A3' },
];

const PROMOTION_TYPES = [
  { value: 'offer', label: 'Flash Offer' },
  { value: 'store_showcase', label: 'Store Showcase' },
  { value: 'seasonal', label: 'Seasonal Deal' },
  { value: 'custom', label: 'Custom' },
];

const defaultForm = {
  type: 'offer',
  storeName: '',
  description: '',
  discount: '',
  color: '#065F46',
  status: 'active',
  imageId: '',
};

// ── Helpers ─────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ── Sub-components ──────────────────────────────────────────

function StatPill({ icon, label, value, color }) {
  return (
    <div className="promo-stat-pill">
      <div className="promo-stat-pill-icon" style={{ background: color }}>
        <img src={icon} alt="" />
      </div>
      <div>
        <div className="promo-stat-pill-label">{label}</div>
        <div className="promo-stat-pill-value">{value}</div>
      </div>
    </div>
  );
}

function PromotionCard({ promo, onEdit, onDelete, onToggle, canEdit, canDelete }) {
  const d = promo.data || promo;
  const type = PROMOTION_TYPES.find((t) => t.value === d.type) || { label: d.type || 'Offer' };
  const color = d.color || '#065F46';
  const status = d.status || 'active';
  const discount = d.discount;
  const imageUrl = d.imageId ? getPromotionImageUrl(d.imageId) : null;

  const bannerStyle = {
    backgroundColor: imageUrl ? 'transparent' : color,
    backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <div className="promo-card">
      <div className="promo-card-banner" style={bannerStyle}>
        {imageUrl && <div className="promo-card-banner-overlay" />}
        <div style={{ position: 'relative' }}>
          <div className="promo-card-banner-type">{type.label}</div>
          <div className="promo-card-banner-title">{d.storeName || 'Promotion'}</div>
        </div>
        {discount > 0 && (
          <div className="promo-card-banner-discount" style={{ position: 'relative' }}>{discount}% OFF</div>
        )}
      </div>
      <div className="promo-card-body">
        {d.description && (
          <div className="promo-card-desc">{d.description}</div>
        )}
        <div className="promo-card-meta">
          <span className={`promo-card-status ${status}`}>
            {status === 'active' ? '● Active' : '○ Inactive'}
          </span>
          <div className="promo-card-actions">
            {canEdit && (
              <button
                className={`promo-action-btn ${status === 'active' ? '' : 'success'}`}
                title={status === 'active' ? 'Deactivate' : 'Activate'}
                onClick={() => onToggle(promo.$id || promo.rowId, status)}
              >
                {status === 'active' ? '⏸' : '▶'}
              </button>
            )}
            {canEdit && (
              <button
                className="promo-action-btn"
                title="Edit"
                onClick={() => onEdit(promo)}
              >
                ✎
              </button>
            )}
            {canDelete && (
              <button
                className="promo-action-btn danger"
                title="Delete"
                onClick={() => onDelete(promo.$id || promo.rowId)}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="promo-card-date">Created {fmtDate(d.createdAt || promo.$createdAt)}</div>
      </div>
    </div>
  );
}

function ApplicationRow({ app, onApprove, onReject, canApprove }) {
  const data = app.data || app;
  const status = data.status || 'pending';
  return (
    <tr>
      <td>
        <strong>{data.storeName || '—'}</strong>
      </td>
      <td style={{ color: 'var(--text-secondary)', maxWidth: 260 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.message || '—'}
        </div>
      </td>
      <td>{fmtDate(data.createdAt || app.$createdAt)}</td>
      <td>
        <span className={`promo-app-status ${status}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </td>
      <td>
        {status === 'pending' && canApprove && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-primary"
              style={{ padding: '6px 14px', fontSize: 12 }}
              onClick={() => onApprove(app.$id || app.rowId, data)}
            >
              Approve
            </button>
            <button
              className="btn-danger"
              style={{ padding: '6px 14px', fontSize: 12 }}
              onClick={() => onReject(app.$id || app.rowId)}
            >
              Reject
            </button>
          </div>
        )}
        {status !== 'pending' && (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            {fmtDate(data.reviewedAt)}
          </span>
        )}
      </td>
    </tr>
  );
}

// ── Create/Edit Modal ────────────────────────────────────────

function PromotionModal({ initial, onSave, onClose, saving }) {
  const [form, setForm]               = useState(initial || defaultForm);
  const [imageFile, setImageFile]     = useState(null);
  const [previewUrl, setPreviewUrl]   = useState(
    initial?.imageId ? getPromotionImageUrl(initial.imageId) : null
  );
  const [dragOver, setDragOver]       = useState(false);
  const fileRef                       = React.useRef();

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const pickFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image or GIF file.'); return; }
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    set('imageId', '');
  };

  const removeImage = () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setImageFile(null);
    setPreviewUrl(null);
    set('imageId', '');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, discount: Number(form.discount) || 0 }, imageFile);
  };

  return (
    <div className="promo-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="promo-modal">
        <div className="promo-modal-header">
          <h2 className="promo-modal-title">
            {initial ? 'Edit Promotion' : 'Create Promotion'}
          </h2>
          <button className="promo-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="promo-modal-body">
            <div className="promo-form-row">
              <label className="promo-form-label">Type</label>
              <select
                className="promo-form-select"
                value={form.type}
                onChange={(e) => set('type', e.target.value)}
                required
              >
                {PROMOTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="promo-form-row">
              <label className="promo-form-label">Store / Brand Name</label>
              <input
                className="promo-form-input"
                placeholder="e.g. Beetle Agrovets"
                value={form.storeName}
                onChange={(e) => set('storeName', e.target.value)}
                required
              />
            </div>

            <div className="promo-form-row">
              <label className="promo-form-label">Description</label>
              <textarea
                className="promo-form-textarea"
                placeholder="Short promo message shown on banner…"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>

            <div className="promo-form-two-col">
              <div className="promo-form-row">
                <label className="promo-form-label">Discount %</label>
                <input
                  className="promo-form-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={form.discount}
                  onChange={(e) => set('discount', e.target.value)}
                />
              </div>
              <div className="promo-form-row">
                <label className="promo-form-label">Status</label>
                <select
                  className="promo-form-select"
                  value={form.status}
                  onChange={(e) => set('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Image / GIF upload */}
            <div className="promo-form-row">
              <label className="promo-form-label">Banner Image / GIF <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optional — replaces solid color)</span></label>

              {previewUrl ? (
                <div className="promo-img-preview-wrap">
                  <img src={previewUrl} alt="preview" className="promo-img-preview" />
                  <button type="button" className="promo-img-remove" onClick={removeImage} title="Remove image">✕</button>
                  <span className="promo-img-replace" onClick={() => fileRef.current?.click()}>Replace</span>
                </div>
              ) : (
                <div
                  className={`promo-upload-zone${dragOver ? ' drag-over' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <div className="promo-upload-icon">🖼️</div>
                  <div className="promo-upload-text">Click or drag an image / GIF here</div>
                  <div className="promo-upload-hint">PNG, JPG, WebP, GIF · max 5 MB</div>
                </div>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*,.gif"
                style={{ display: 'none' }}
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </div>

            <div className="promo-form-row">
              <label className="promo-form-label">Banner Color <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(used when no image)</span></label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    title={c.label}
                    onClick={() => set('color', c.value)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 7,
                      background: c.value,
                      border: form.color === c.value ? '3px solid var(--text-primary)' : '2px solid transparent',
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                  />
                ))}
              </div>
              <div className="promo-color-preview">
                <div className="promo-color-swatch" style={{ background: form.color }} />
                <input
                  className="promo-form-input"
                  type="text"
                  value={form.color}
                  onChange={(e) => set('color', e.target.value)}
                  placeholder="#065F46"
                  style={{ flex: 1 }}
                />
              </div>
            </div>

            {/* Live preview */}
            <div style={{
              marginTop: 8,
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: previewUrl ? '#000' : form.color,
              backgroundImage: previewUrl ? `url(${previewUrl})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: 80,
              display: 'flex',
              alignItems: 'center',
              padding: '0 18px',
              gap: 12,
              position: 'relative',
            }}>
              {previewUrl && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.38)', borderRadius: 12 }} />
              )}
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  {PROMOTION_TYPES.find((t) => t.value === form.type)?.label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>
                  {form.storeName || 'Preview'}
                </div>
              </div>
              {form.discount > 0 && (
                <div style={{
                  position: 'absolute', right: 18,
                  background: 'rgba(255,255,255,0.22)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 20, padding: '4px 10px',
                  fontSize: 12, fontWeight: 700, color: '#fff',
                  zIndex: 1,
                }}>
                  {form.discount}% OFF
                </div>
              )}
            </div>
          </div>
          <div className="promo-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : initial ? 'Save Changes' : 'Create Promotion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Reject Modal ─────────────────────────────────────────────

function RejectModal({ onConfirm, onClose, saving }) {
  const [reason, setReason] = useState('');
  return (
    <div className="promo-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="promo-modal promo-reject-modal">
        <div className="promo-modal-header">
          <h2 className="promo-modal-title">Reject Application</h2>
          <button className="promo-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="promo-modal-body">
          <div className="promo-form-row">
            <label className="promo-form-label">Reason (optional)</label>
            <textarea
              className="promo-form-textarea"
              placeholder="Provide a reason visible to the store owner…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <div className="promo-modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-danger"
            disabled={saving}
            onClick={() => onConfirm(reason)}
          >
            {saving ? 'Rejecting…' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────

export default function PromotionsPage() {
  const { adminRole } = useOutletContext?.() || {};
  const canCreate = can(adminRole, 'create', 'promotions');
  const canEdit = can(adminRole, 'update', 'promotions');
  const canDelete = can(adminRole, 'delete', 'promotions');
  const canApprove = can(adminRole, 'approve', 'promotions');

  const [activeTab, setActiveTab] = useState('active');
  const [promotions, setPromotions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [promos, apps] = await Promise.all([
        listPromotions().catch(() => []),
        listStoreApplications().catch(() => []),
      ]);
      setPromotions(promos);
      setApplications(apps);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Promotion CRUD ──

  const handleSave = async (formData, imageFile) => {
    setSaving(true);
    try {
      let imageId = formData.imageId || '';

      if (imageFile) {
        // Delete old image if replacing
        const oldId = editTarget ? (editTarget.data || editTarget).imageId : '';
        if (oldId) await deletePromotionImage(oldId).catch(() => {});
        imageId = await uploadPromotionImage(imageFile);
      }

      const dataToSave = { ...formData, imageId };

      if (editTarget) {
        await updatePromotion(editTarget.$id || editTarget.rowId, dataToSave);
      } else {
        await createPromotion(dataToSave);
      }
      setShowModal(false);
      setEditTarget(null);
      await load();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion? This cannot be undone.')) return;
    try {
      await deletePromotion(id);
      await load();
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await togglePromotionStatus(id, currentStatus);
      await load();
    } catch (err) {
      alert(err.message || 'Toggle failed');
    }
  };

  const openEdit = (promo) => {
    setEditTarget(promo);
    setShowModal(true);
  };

  // ── Application handling ──

  const handleApprove = async (id, data) => {
    setSaving(true);
    try {
      await approveApplication(id, data);
      await load();
    } catch (err) {
      alert(err.message || 'Approval failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!rejectTarget) return;
    setSaving(true);
    try {
      await rejectApplication(rejectTarget, reason);
      setRejectTarget(null);
      await load();
    } catch (err) {
      alert(err.message || 'Rejection failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Stats ──

  const getStatus = (p) => (p.data || p).status;
  const activeCount = promotions.filter((p) => getStatus(p) === 'active').length;
  const pendingCount = applications.filter((a) => (getStatus(a) || 'pending') === 'pending').length;

  // ── Filtered data for current tab ──

  const filteredPromos = activeTab === 'active'
    ? promotions.filter((p) => getStatus(p) === 'active')
    : activeTab === 'inactive'
    ? promotions.filter((p) => getStatus(p) !== 'active')
    : promotions;

  return (
    <div className="promo-page">
      {/* Header */}
      <div className="promo-page-header">
        <div>
          <h1 className="promo-page-title">Promotions</h1>
          <p className="promo-page-sub">
            Manage banners that appear on the customer home screen
          </p>
        </div>
        <div className="promo-header-actions">
          <button className="btn-secondary" onClick={load} title="Refresh">
            ↻ Refresh
          </button>
          {canCreate && (
            <button
              className="btn-primary"
              onClick={() => { setEditTarget(null); setShowModal(true); }}
            >
              + New Promotion
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="promo-stats-row">
        <StatPill
          icon="/icons/Notification.svg"
          label="Total"
          value={promotions.length}
          color="#3b82f6"
        />
        <StatPill
          icon="/icons/fi_check.svg"
          label="Active"
          value={activeCount}
          color="#039855"
        />
        <StatPill
          icon="/icons/Bag.svg"
          label="Applications"
          value={pendingCount}
          color="#f59e0b"
        />
      </div>

      {/* Error */}
      {error && <div className="promo-error">{error}</div>}

      {/* Tabs */}
      <div className="promo-tabs">
        {[
          { key: 'active', label: 'Active', count: activeCount },
          { key: 'inactive', label: 'Inactive', count: promotions.length - activeCount },
          { key: 'all', label: 'All Promotions', count: promotions.length },
          { key: 'applications', label: 'Store Applications', count: pendingCount },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`promo-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="promo-tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="promo-loading">
          <div className="promo-spinner" />
          Loading promotions…
        </div>
      ) : activeTab !== 'applications' ? (
        filteredPromos.length === 0 ? (
          <div className="promo-empty">
            <div className="promo-empty-icon">📣</div>
            <div className="promo-empty-title">No promotions here</div>
            <div className="promo-empty-sub">
              {canCreate
                ? 'Create your first promotion to get started.'
                : 'No promotions in this category yet.'}
            </div>
          </div>
        ) : (
          <div className="promo-grid">
            {filteredPromos.map((promo) => (
              <PromotionCard
                key={promo.$id || promo.rowId}
                promo={promo}
                onEdit={openEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
                canEdit={canEdit}
                canDelete={canDelete}
              />
            ))}
          </div>
        )
      ) : (
        applications.length === 0 ? (
          <div className="promo-empty">
            <div className="promo-empty-icon">🏪</div>
            <div className="promo-empty-title">No store applications</div>
            <div className="promo-empty-sub">
              Stores apply here to be featured in customer banners.
            </div>
          </div>
        ) : (
          <table className="promo-applications-table">
            <thead>
              <tr>
                <th>Store</th>
                <th>Message</th>
                <th>Applied</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <ApplicationRow
                  key={app.$id || app.rowId}
                  app={app}
                  onApprove={handleApprove}
                  onReject={(id) => setRejectTarget(id)}
                  canApprove={canApprove}
                />
              ))}
            </tbody>
          </table>
        )
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <PromotionModal
          initial={editTarget ? (() => { const d = editTarget.data || editTarget; return {
            type: d.type || 'offer',
            storeName: d.storeName || '',
            description: d.description || '',
            discount: d.discount ?? '',
            color: d.color || '#065F46',
            status: d.status || 'active',
          }; })() : null}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
          saving={saving}
        />
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          onConfirm={handleRejectConfirm}
          onClose={() => setRejectTarget(null)}
          saving={saving}
        />
      )}
    </div>
  );
}
